import { useState, useEffect, useRef } from "react";
import { Lock, TrendingUp, AlertTriangle, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { ResponsiveTooltip } from "@/components/ui/responsive-tooltip";
import { scrollToWaitlist } from "@/lib/analytics";
import type { ScoringResult } from "@/lib/scoring";

interface DualScoreVisualizationProps {
  scoring: ScoringResult;
  brandName: string;
  websiteUrl?: string;
  skipAnimation?: boolean;
  isLoading?: boolean;
}

const LOADING_MESSAGES = [
  { delay: 0, message: "Scanning ChatGPT..." },
  { delay: 3000, message: "Checking Google..." },
  { delay: 6000, message: "Analyzing signals..." },
  { delay: 9000, message: "Almost there..." },
];

// Easing function for smooth animation (ease-out-quart)
const easeOutQuart = (t: number): number => {
  return 1 - Math.pow(1 - t, 4);
};

// Unified color logic based on score (0-100 scale)
const getScoreTheme = (score: number) => {
  if (score >= 50) {
    return {
      colorClass: "text-emerald-500",
      strokeClass: "stroke-emerald-500",
      bgClass: "bg-emerald-500",
      bannerBg: "bg-emerald-50 dark:bg-emerald-950/30",
      bannerBorder: "border-emerald-200 dark:border-emerald-800",
      bannerText: "text-emerald-700 dark:text-emerald-300",
      statusText: "Strong Foundation",
      bannerMessage: "Visible on scanned platforms. Want the full picture? Request an audit.",
      BannerIcon: TrendingUp,
      gradientStart: "hsl(152 76% 36%)",
      gradientEnd: "hsl(152 76% 45%)",
    };
  } else if (score >= 30) {
    return {
      colorClass: "text-amber-500",
      strokeClass: "stroke-amber-500",
      bgClass: "bg-amber-500",
      bannerBg: "bg-amber-50 dark:bg-amber-950/30",
      bannerBorder: "border-amber-200 dark:border-amber-800",
      bannerText: "text-amber-700 dark:text-amber-300",
      statusText: "Partial Visibility",
      bannerMessage: "Room for improvement. Consider SEO and GEO optimization",
      BannerIcon: AlertTriangle,
      gradientStart: "hsl(38 92% 50%)",
      gradientEnd: "hsl(45 93% 55%)",
    };
  }
  return {
    colorClass: "text-rose-500",
    strokeClass: "stroke-rose-500",
    bgClass: "bg-rose-500",
    bannerBg: "bg-rose-50 dark:bg-rose-950/30",
    bannerBorder: "border-rose-200 dark:border-rose-800",
    bannerText: "text-rose-700 dark:text-rose-300",
    statusText: "Low Visibility",
    bannerMessage: "Significant work needed to improve AI visibility",
    BannerIcon: AlertCircle,
    gradientStart: "hsl(0 84% 60%)",
    gradientEnd: "hsl(0 72% 51%)",
  };
};

// Helper to get favicon URL from Google's favicon service
const getFaviconUrl = (url: string | undefined, _brand: string): string | null => {
  if (!url || url.trim() === '') return null;
  try {
    const domain = url.includes('://') 
      ? new URL(url).hostname 
      : new URL(`https://${url}`).hostname;
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
  } catch {
    return null;
  }
};

// Helper to get consistent color from brand name
const getBrandColor = (brand: string): string => {
  const colors = [
    'bg-violet-500', 'bg-blue-500', 'bg-emerald-500', 
    'bg-amber-500', 'bg-rose-500', 'bg-cyan-500'
  ];
  const index = brand.charCodeAt(0) % colors.length;
  return colors[index];
};

const DualScoreVisualization = ({ 
  scoring, 
  brandName, 
  websiteUrl,
  skipAnimation = false,
  isLoading = false 
}: DualScoreVisualizationProps) => {
  const [displayScore, setDisplayScore] = useState(0);
  const [animationProgress, setAnimationProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(LOADING_MESSAGES[0].message);
  const [verifiedFavicon, setVerifiedFavicon] = useState<string | null>(null);
  const animationRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  
  const faviconUrl = getFaviconUrl(websiteUrl, brandName);
  const lockedPoints = 30;
  const totalScale = 100;

  // Preload favicon
  useEffect(() => {
    if (!faviconUrl) {
      setVerifiedFavicon(null);
      return;
    }
    const img = new Image();
    img.src = faviconUrl;
    img.onload = () => setVerifiedFavicon(faviconUrl);
    img.onerror = () => setVerifiedFavicon(null);
  }, [faviconUrl]);

  // Dynamic loading messages
  useEffect(() => {
    if (!isLoading) {
      setLoadingMessage(LOADING_MESSAGES[0].message);
      return;
    }
    const timers = LOADING_MESSAGES.slice(1).map(({ delay, message }) =>
      setTimeout(() => setLoadingMessage(message), delay)
    );
    return () => timers.forEach(clearTimeout);
  }, [isLoading]);

  const theme = getScoreTheme(scoring.currentScore);

  // Ring calculations
  const svgSize = 220;
  const strokeWidth = 16;
  const radius = (svgSize - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  
  // Calculate segments
  const earnedPercentage = (animationProgress / 100) * (scoring.currentScore / totalScale) * 100;
  const lockedPercentage = (lockedPoints / totalScale) * 100;
  
  const earnedDash = (earnedPercentage / 100) * circumference;
  const lockedDash = (lockedPercentage / 100) * circumference;

  const gradientId = `scoreGradient-${scoring.currentScore}`;

  useEffect(() => {
    if (isLoading) {
      setDisplayScore(0);
      setAnimationProgress(0);
      setIsComplete(false);
      return;
    }

    if (!scoring.currentScore) return;

    setDisplayScore(0);
    setAnimationProgress(0);
    startTimeRef.current = null;

    const duration = 1500;
    
    const animate = (timestamp: number) => {
      if (!startTimeRef.current) startTimeRef.current = timestamp;
      
      const elapsed = timestamp - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutQuart(progress);
      
      setAnimationProgress(easedProgress * 100);
      setDisplayScore(Math.round(easedProgress * scoring.currentScore));
      
      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setIsComplete(true);
      }
    };
    
    const startDelay = setTimeout(() => {
      animationRef.current = requestAnimationFrame(animate);
    }, 200);
    
    return () => {
      clearTimeout(startDelay);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [scoring.currentScore, isLoading]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center gap-6">
        <h2 className="text-lg md:text-xl lg:text-2xl text-muted-foreground text-center">
          AI Visibility Score for <span className="text-primary font-semibold">{brandName}</span>
        </h2>
        <div className="flex flex-col items-center">
          <div className="w-48 h-48 rounded-full bg-muted/30 animate-pulse flex items-center justify-center">
            <span className="text-sm text-muted-foreground animate-pulse text-center px-4 leading-tight">
              {loadingMessage}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-4 py-4 animate-scale-in">
      {/* Header with brand */}
      <h2 className="text-xl md:text-2xl lg:text-3xl text-muted-foreground text-center">
        <span>AI Visibility Score for </span>
        <span className="inline-flex items-center gap-2">
          <div className="relative w-6 h-6 md:w-7 md:h-7 flex-shrink-0">
            {verifiedFavicon ? (
              <img 
                src={verifiedFavicon}
                alt=""
                className="w-full h-full rounded-md object-contain bg-white"
              />
            ) : (
              <div className={cn(
                "w-full h-full rounded-md flex items-center justify-center text-white text-sm font-bold shadow-sm",
                getBrandColor(brandName)
              )}>
                {brandName.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <span className="text-primary font-semibold underline decoration-primary decoration-2 underline-offset-4">
            {brandName}
          </span>
        </span>
      </h2>

      {/* Single Score Ring */}
      <div className="flex flex-col items-center gap-2">
        <div className="relative flex items-center justify-center" style={{ width: svgSize, height: svgSize }}>
          {/* Glow effect */}
          <div 
            className={cn(
              "absolute inset-0 rounded-full blur-xl transition-opacity duration-1000",
              isComplete ? "opacity-40" : "opacity-20"
            )}
            style={{ 
              background: `radial-gradient(circle, ${theme.gradientStart} 0%, transparent 70%)`,
            }}
          />
          
          <svg className="absolute transform -rotate-90 w-full h-full" viewBox={`0 0 ${svgSize} ${svgSize}`}>
            <defs>
              <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor={theme.gradientStart} />
                <stop offset="100%" stopColor={theme.gradientEnd} />
              </linearGradient>
            </defs>
            
            {/* Background ring */}
            <circle
              cx={svgSize / 2}
              cy={svgSize / 2}
              r={radius}
              fill="none"
              stroke="hsl(var(--secondary))"
              strokeWidth={strokeWidth}
              className="opacity-40"
            />
            
            {/* Locked segment */}
            <circle
              cx={svgSize / 2}
              cy={svgSize / 2}
              r={radius}
              fill="none"
              stroke="hsl(var(--muted-foreground))"
              strokeWidth={strokeWidth}
              strokeDasharray="8 4"
              strokeDashoffset={-earnedDash}
              strokeLinecap="round"
              className="opacity-30"
            />
            
            {/* Earned segment */}
            <circle
              cx={svgSize / 2}
              cy={svgSize / 2}
              r={radius}
              fill="none"
              stroke={`url(#${gradientId})`}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeDasharray={`${earnedDash} ${circumference - earnedDash}`}
              style={{ transition: 'stroke-dashoffset 0.05s linear' }}
            />
          </svg>
          
          {/* Center content */}
          <div className="text-center z-10 select-none">
            <span className={cn("text-5xl md:text-6xl font-bold tabular-nums select-none", theme.colorClass)}>
              {displayScore}
            </span>
            <span className="text-2xl text-muted-foreground select-none">/100</span>
          </div>
        </div>
        
        {/* Label */}
        <span className="text-base font-medium text-foreground">Current Visibility</span>
        
        {/* Legend */}
        <div className="flex items-center gap-4 text-sm flex-wrap justify-center">
          <div className="flex items-center gap-1.5">
            <div 
              className="w-3 h-3 rounded-sm"
              style={{ backgroundColor: theme.gradientStart }}
            />
            <span className="text-muted-foreground">Scanned ({displayScore})</span>
          </div>
          {scoring.currentScore < 70 && (
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-sm bg-secondary/50 border border-border" />
              <span className="text-muted-foreground">Available (+{70 - scoring.currentScore})</span>
            </div>
          )}
          <ResponsiveTooltip content={<p className="text-sm">Included in the full audit</p>} side="top">
            <div 
              className="flex items-center gap-1.5 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => scrollToWaitlist()}
            >
              <div className="w-3 h-3 rounded-sm bg-muted-foreground/40 flex items-center justify-center">
                <Lock className="w-2 h-2 text-muted-foreground" />
              </div>
              <span className="text-muted-foreground">Locked (+{lockedPoints})</span>
            </div>
          </ResponsiveTooltip>
        </div>
      </div>

      {/* Status Banner */}
      <div className={cn(
        "flex items-center gap-2 px-4 py-2 rounded-lg border",
        theme.bannerBg,
        theme.bannerBorder,
        theme.bannerText
      )}>
        <theme.BannerIcon className="w-4 h-4" />
        <span className="text-sm font-medium">{theme.bannerMessage}</span>
      </div>

    </div>
  );
};

export default DualScoreVisualization;
